import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { ProductsPartialState } from './products.reducer';
import { ProductsQuery } from './products.selectors';
import * as ProductsActions from './products.actions';
import { ActionWithCorrelationAttributes } from '@kion/ngrx-helper';
import { MasterProductComplete, Product } from '@kion/data-models';
import { Dictionary } from '@ngrx/entity';
import { Enricher } from '@kion/rxjs-helper';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

 type ProductCompleteEnrichments = {
  products: Product[];
 }

 class ProductEnricher extends Enricher<Product, MasterProductComplete, ProductCompleteEnrichments> {

  constructor(productsFacade: ProductsFacade) {
    super();
    this.enrichments$ = combineLatest(
      productsFacade.all$
    ).pipe(
      map(([products]) => (
        { products }
      ))
    );
  }

  doEnrich(product: Product, enrichments: ProductCompleteEnrichments): MasterProductComplete {
    return {
      masterProduct: product,
      variants: enrichments.products?.filter( p => p?.baseProduct === product?.id)
    }
  }
}

@Injectable()
export class ProductsFacade {

  all$ = this.store.pipe(select(ProductsQuery.getAllProducts));
  allById$ = this.store.pipe(select(ProductsQuery.getAllProductsById));
  searchResults$ = this.store.pipe(select(ProductsQuery.getSearchResults));
  textSearchResults$ = this.store.pipe(select(ProductsQuery.getTextSearchResults));
  textSearchTotal$ = this.store.pipe(select(ProductsQuery.getTextSearchTotal));
  isSearching$ = this.store.pipe(select(ProductsQuery.getIsSearching));
  lastCreatedId$ = this.store.pipe(select(ProductsQuery.getLastCreatedId));
  facets$ = this.store.pipe(select(ProductsQuery.getFacets));
  sorts$ = this.store.pipe(select(ProductsQuery.getSorts));
  pagination$ = this.store.pipe(select(ProductsQuery.getPagination));
  references$ = this.store.pipe(select(ProductsQuery.getReferences));
  activeProductId$ = this.store.pipe(select(ProductsQuery.getActiveProductId));
  lastProduct$ = this.store.pipe(select(ProductsQuery.getById));
  searchTerm$ = this.store.pipe(select(ProductsQuery.getSearchTerm));
  noResult$ = this.store.pipe(select(ProductsQuery.getNoResult));
  query$ = this.store.pipe(select(ProductsQuery.getQuery));

  enricher: ProductEnricher;


  constructor(private store: Store<ProductsPartialState>) {
    this.enricher = new ProductEnricher(this)
  }

  protected dispatch(action, correlationId?: string): ActionWithCorrelationAttributes {
    if (correlationId) {
      action.__correlationId = correlationId;
    }
    this.store.dispatch(action);
    return action;
  }

  search(query?: string, correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(ProductsActions.search({ query }), correlationId);
  }
  textSearch(query?: string, correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(ProductsActions.textSearch({ query, sort: 'relevance' }), correlationId);
  }
  loadById(id: string, correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch((ProductsActions.loadById({ id: id })), correlationId);
  }
  selectVariant(
    baseSite: string,
    variantId: string,
    product: Product,
    correlationId?: string
  ): ActionWithCorrelationAttributes {
    return this.dispatch(
      ProductsActions.selectProductVariant({ variantId: variantId, product: product }),
      correlationId
    );
  }
  loadByIds(ids: string[], correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      ProductsActions.loadByIds({ ids: ids }),
      correlationId
    )
  }
  updateQuery(queryPart: string, remove?: boolean, clearAll?: boolean,  correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      ProductsActions.updateQuery({ queryPart: queryPart, remove: remove, clearAll: clearAll }),
      correlationId
    )
  };
  updateSort(sort: string, correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      ProductsActions.updateSort({ sort: sort }),
      correlationId
    )
  }
  updatePerPage(perPage: number, correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      ProductsActions.updatePerPage({ perPage: perPage }),
      correlationId
    );
  }
  updatePage(page: number, correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      ProductsActions.updatePage({ page: page }),
      correlationId
    );
  }
  loadReferences(productId: string, references: string[], correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      ProductsActions.loadProductReference({ productId: productId, references: references }),
      correlationId
    );
  }
  setProductActive(productId: string, correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      ProductsActions.setActiveProduct({ productId: productId }),
      correlationId
    );
  }
  setSearchTerm(searchTerm: string, correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      ProductsActions.setSearchTerm({ searchTerm: searchTerm }),
      correlationId
    )
  }

  textSearchReset(correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      ProductsActions.textSearchReset(),
      correlationId
    )
  }
  setNoResult(toggle: boolean = false, correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      ProductsActions.setNoResult({toggle: toggle}),
      correlationId
    )
  }
}
